<template>
  <div
    v-if="isEnabled && providersEnabledCount > 0"
    class="mt-8"
  >
    <div class="mt-6 relative">
      <div class="absolute inset-0 flex items-center">
        <div class="w-full border-t border-neutral-200" />
      </div>
      <div class="relative flex justify-center text-sm leading-5">
        <span class="uppercase px-2 bg-white text-neutral-700">
          {{ $t('common.or') }}
        </span>
      </div>
    </div>
    <div class="mt-8 grid gap-x-4 gap-y-2">
      <div
        v-for="provider in providers"
        :key="provider"
      >
        <div v-if="$statsig.ready && $statsig.checkGate(`auth_social_${provider}`)">
          <span class="w-full inline-flex rounded-full shadow-sm">
            <button
              type="button"
              :aria-label="$t(`login.continue-with-${provider}`)"
              :class="{
                'cursor-not-allowed opacity-50': isSocialiteLoading,
                'hover:text-neutral-400': !isSocialiteLoading,
              }"
              :content="$t(`login.continue-with-${provider}`)"
              :disabled="isSocialiteLoading"
              class="
                w-full inline-flex items-center justify-center py-3 px-4 border border-neutral-900 rounded-full bg-white text-md
                leading-5 font-medium text-neutral-900 focus:outline-none focus:border-blue-300 focus:ring-blue transition
              "
              @click="socialiteAuth(socialiteProvider[provider])"
            >
              <fa
                :icon="['fab', provider]"
                class="fa-fw text-xl"
              />
              <span class="ml-2 font-bold">{{ $t(`login.continue-with-${provider}`) }}</span>
            </button>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faApple,
  faFacebook,
  faGoogle,
} from '@fortawesome/free-brands-svg-icons';
import merge from 'lodash/merge';
import { mapActions, mapGetters } from 'vuex';

import AuthFormType from '@/enums/AuthFormType';
import ParseErrors from '@/mixins/ParseErrors';
import SocialiteActions from '@/mixins/SocialiteActions';
import installApplePlugin from '@/socialite/apple';
import initFacebookSdk from '@/socialite/facebook';
import { initializeGooglePlugin } from '@/socialite/google';

library.add({
  faApple,
  faFacebook,
  faGoogle,
});

export default {
  name: 'VetsterAuthSocialiteButtons',

  mixins: [
    ParseErrors,
    SocialiteActions,
  ],

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters('socialite', [
      'isLoading',
      'isOpenForRegistration',
    ]),
    isEnabled() {
      return this.$statsig.ready && this.$statsig.checkGate('auth_social_all');
    },
    isSocialiteLoading() {
      return this.isLoading || this.loading;
    },
    providersEnabledCount() {
      return this.$statsig.ready ? [
        this.$statsig.checkGate('auth_social_apple'),
        this.$statsig.checkGate('auth_social_facebook'),
        this.$statsig.checkGate('auth_social_google'),
      ].filter(Boolean).length : 0;
    },
    providers() {
      return ['apple', 'facebook', 'google'];
    },
  },

  watch: {
    '$statsig.ready': {
      handler(ready) {
        if (ready) {
          this.installSocialitePlugins();
        }
      },
    },
  },

  mounted() {
    if (this.$statsig.ready) {
      this.installSocialitePlugins();
    }
  },

  methods: {
    ...mapActions(['checkAuthAndUser']),

    installSocialitePlugins() {
      if (!this.isEnabled) {
        return;
      }

      if (this.$statsig.checkGate('auth_social_apple')) {
        installApplePlugin();
      }

      if (this.$statsig.checkGate('auth_social_facebook')) {
        initFacebookSdk().catch(() => {});
      }

      if (this.$statsig.checkGate('auth_social_google')) {
        initializeGooglePlugin();
      }
    },

    async socialiteAuth(provider) {
      if (!this.isEnabled) {
        return;
      }

      this.$store.commit('socialite/setLoading', true);

      try {
        const accessToken = await this.socialiteSignIn(provider);

        if (!accessToken) {
          this.displayError(this.$t('login.woof-unable-to-connect-to-socialite', { provider: provider.label }));

          return;
        }

        this.$analytics.trackGA({ eventName: 'gx:identity:login:submit-attempt' });

        await this.$store.dispatch('socialite/authenticate', merge({
          provider_type: provider.value,
          access_token: accessToken,
          platform: 'WEB',
        }, this.registrationPayload)).then(async () => {
          if (await this.$auth.authenticate()) {
            this.$toast(this.$t('login.welcome-back'));
            await this.checkAuthAndUser();
            this.$analytics.trackSignIn(provider.value);
            this.$emit('action-completed', 'socialite-auth');
          } else if (this.isOpenForRegistration) {
            this.$emit('switch', AuthFormType.Socialite);
          }
        }).catch(async (error) => {
          if (error?.response?.data?.data?.requires_verification) {
            this.$store.commit('emailVerification/setSocialite', {
              provider_type: provider.value,
              access_token: accessToken,
            });
            this.$store.commit('emailVerification/showEmailVerification', true);
            this.$store.commit('setXsrfToken', error.response);
          } else {
            this.showError(error, true);
            await this.socialiteSignOut(provider);
            this.$analytics.trackSignInFailure(provider.value);
          }
        });
      } catch (error) {
        if (!this.isSocialitePopupClosed(error)) {
          this.displayError(this.$t('login.woof-unable-to-connect-to-socialite', { provider: provider.label }));

          if (error instanceof Error) {
            this.$sentry.captureException(error);
          }
        }
        await this.socialiteSignOut(provider);
        this.$store.commit('emailVerification/setSocialite', null);
        this.$analytics.trackSignInFailure(provider.value);
      } finally {
        this.$store.commit('socialite/setLoading', false);
      }
    },
  },
};
</script>
